/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { IDictionary, IRequestContext } from '@msdyn365-commerce/core';
import { ProductBehavior } from '@msdyn365-commerce/retail-proxy';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DEFAULT_MAX_QUANTITY } from '../../../../utilities/quantity-constants';

export enum VariantType {
    Browser,
    Viewport
}

/**
 * Returns the payload Object.
 * @param {string} contentCategory - Content Category.
 * @param {IModule} props - Module props.
 * @param {string} moduleName - Module Name.
 * @param eventType
 * @param telemetryContent
 * @param {string} etext - Etext Name.
 * @param {string} recid - Recid Name.
 */
export function getPayloadObject(eventType: string, telemetryContent: ITelemetryContent, etext: string, recid?: string): IPayLoad {
    const pageName = telemetryContent && telemetryContent.pageName ? telemetryContent.pageName : '';
    const moduleName = telemetryContent && telemetryContent.moduleName ? telemetryContent.moduleName : '';
    const payload: IPayLoad = {
        contentCategory: eventType,
        contentAction: {
            pgname: pageName,
            mname: moduleName,
            etext,
            recid,
            etype: eventType
        }
    };
    return payload;
}

export interface IMobileProps {
    variant?: VariantType;
    context?: IRequestContext;
}

export const isMobile = (props: IMobileProps) => {
    const { variant, context } = props;
    switch (variant) {
        case VariantType.Browser:
            if (MsDyn365.isBrowser && window.innerWidth) {
                const gridSettings = context?.gridSettings;
                if (gridSettings) {
                    if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                        return 'xs';
                    } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                        return 'sm';
                    } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                        return 'md';
                    } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                        return 'lg';
                    }
                    return 'xl';
                }
            }

            return 'lg';
        case VariantType.Viewport:
            const viewport = context?.device?.Type === 'Mobile' ? 'xs' : 'lg';
            return viewport;
        default:
            return 'lg';
    }
};

/**
 * Returns the telemetry Attributes.
 * @param {IModule} props - Module Props.
 * @param telemetryContent
 * @param {IPayLoad} payLoad - Pay Load.
 */
export function getTelemetryAttributes(telemetryContent: ITelemetryContent, payLoad: IPayLoad): IDictionary<string> | undefined {
    const contentData = payLoad.contentAction.recid ? payLoad.contentAction.recid : payLoad.contentAction.etext;
    const attributes: IDictionary<string> | undefined =
        telemetryContent &&
        telemetryContent.telemetry &&
        telemetryContent.telemetry.setTelemetryAttribute &&
        telemetryContent.telemetry.setTelemetryAttribute(contentData!, payLoad.contentAction);
    return attributes;
}

export interface IContentAction {
    pgname: string;
    mname: string;
    etext?: string;
    recid?: string;
    etype?: string;
}

export interface IPayLoad {
    contentCategory: string;
    contentAction: IContentAction;
}

export enum TelemetryConstant {
    ShopSimliarLooks = 'shop similar looks',
    ShopSimliarDescription = 'shop similar description',
    PickupInStore = 'pick up in store',
    PreferredStore = 'set as preferred store',
    RemovePreferredStore = 'remove preferred store',
    IncrementQuantity = 'Increment quantity',
    DecrementQuantity = 'Decrement quantity',
    Checkout = 'Checkout',
    GuestCheckout = 'Guest checkout',
    BackToShopping = 'Back to shopping',
    ApplyPromoCode = 'Apply promo code',
    ViewMore = 'View more',
    OrderDetails = 'Order details',
    AvailablePoints = 'Available points',
    ExpiringPoints = 'Expiring points',
    AddUser = 'Add user',
    BuyItAgain = 'Buy it again',
    ContactNumber = 'ContactNumber',
    CheckoutChange = 'Change',
    Save = 'Save',
    SaveContinue = 'Save and Continue',
    Cancel = 'Cancel',
    DeliveryOptions = 'Delivery options',
    CheckoutTermsAndConditions = 'Checkout terms and conditions',
    ExpandAll = 'Expand all',
    CollapseAll = 'Collapse all',
    Expand = 'Expand',
    Collapse = 'Collapse',
    SignIn = 'Sign in',
    SignUp = 'Sign up',
    SignOut = 'Sign out',
    SwitchCustomer = 'Switch Customer',
    LoyaltyDetailLink = 'Loyalty detail ink',
    LoyaltyTermsLink = 'Loyalty terms link',
    LoyaltyTermsCancel = 'Loyalty terms cancel',
    LoyaltyTermsSubmit = 'Loyalty terms submit',
    LoyaltyTermsAgree = 'Loyalty terms agree',
    RemoveCartItem = 'Remove cart item',
    EditCart = 'Edit cart',
    BackToTop = 'Back to top',
    Logo = 'Logo',
    NavIcon = 'Navigation icon',
    ViewBagAndCheckout = 'View bag and checkout',
    ContinueShopping = 'Continue shopping',
    AddOrderTemplateToCart = 'Add order template to cart',
    PromoBannerClose = 'Close promo banner',
    WriteReview = 'Write Review',
    Next = 'Next',
    Previous = 'Previous',
    SubmitReview = 'Submit review',
    SuccessButton = 'Success',
    SearchStore = 'Search store',
    ShowAllStore = 'Show all store',
    ToggleMapView = 'Toggle map view',
    PrivacyPolicy = 'Privacy policy',
    ViewShoppingBag = 'View shopping bag',
    MyProfile = 'My profile',
    seeDetails = 'See Details',
    CurrentDistributor = 'Set as current distributor',
    ViewMode = 'View mode',
    ApplyRefiner = 'Apply Refiner',
    Search = 'Search'
}

interface ITelemetryContent {
    pageName: string;
    moduleName: string;
    telemetry?: ITelemetry;
}

interface IIncrementalQuantityState {
    isUpdating: boolean;
    currentInput: string;
}

interface IIncrementalQuantityProps {
    min?: number;
    max: number;
    multipleOfQuantity: number;
    currentCount?: number;
    disabled?: boolean;
    incrementGlyphClass?: string;
    decrementGlyphClass?: string;
    decrementButtonAriaLabel?: string;
    incrementButtonAriaLabel?: string;
    minQuantityText: string;
    maxQuantityText: string;
    inputQuantityAriaLabel?: string;
    isGiftCard?: boolean;
    id?: string;
    telemetryContent?: ITelemetryContent;
    applyDefaultOrderSettings?: boolean;
    onChange?(newValue: number): boolean | void;
    productBehavior: ProductBehavior;
    reset?: boolean;
    handleReset?: () => void;
}
/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class IncrementalQuantity extends React.PureComponent<IIncrementalQuantityProps, IIncrementalQuantityState> {
    public static defaultProps: Partial<IIncrementalQuantityProps> = {
        min: 1,
        max: DEFAULT_MAX_QUANTITY,
        multipleOfQuantity: 0.01,
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus'
    };

    private _minValue: number;
    private _maxValue: number;
    private _multipleQuantityValue: number;

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly payLoad: IPayLoad;

    public static getDerivedStateFromProps(
        props: Readonly<IIncrementalQuantityProps>,
        state: IIncrementalQuantityState
    ): IIncrementalQuantityState {
        const nextState = { ...state };

        if (props.disabled && !state.isUpdating) {
            nextState.isUpdating = true;
        }

        let updatedValue = parseFloat(state.currentInput);

        if (
            (!props.disabled && state.isUpdating) ||
            ((props.applyDefaultOrderSettings ?? false) &&
                updatedValue === 1.0 &&
                props.currentCount &&
                props.currentCount > IncrementalQuantity.defaultProps.min!)
        ) {
            updatedValue = props.currentCount ?? 1.0;
            nextState.isUpdating = false;
        }

        return nextState;
    }

    constructor(props: IIncrementalQuantityProps) {
        super(props);
        let minQuantity = IncrementalQuantity.defaultProps.min!;
        let maxQuantity = IncrementalQuantity.defaultProps.max!;
        let multipleOfQuantity = IncrementalQuantity.defaultProps.multipleOfQuantity!;

        if (props.productBehavior) {
            minQuantity = props.productBehavior.MinimumQuantity ?? IncrementalQuantity.defaultProps.min!;
            multipleOfQuantity = props.productBehavior.MultipleOfQuantity ?? IncrementalQuantity.defaultProps.multipleOfQuantity!;
        }

        // Read MaximumQuantity from productBehavior if available, then from props.max, then DEFAULT_MAX_QUANTITY
        if (props.productBehavior?.MaximumQuantity && props.productBehavior.MaximumQuantity > 0) {
            maxQuantity = props.productBehavior.MaximumQuantity;
        } else if (props.max && props.max > 0) {
            maxQuantity = props.max;
        }

        this.state = {
            isUpdating: false,
            currentInput: props.productBehavior
                ? minQuantity > 0
                    ? minQuantity.toFixed(2)
                    : multipleOfQuantity > 0
                    ? multipleOfQuantity.toFixed(2)
                    : '1.00'
                : props.currentCount?.toFixed(2) ?? '1.00'
        };
        this._onIncrement = this._onIncrement.bind(this);
        this._onDecrement = this._onDecrement.bind(this);
        this._handleChange = this._handleChange.bind(this);
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
        this._minValue = minQuantity ?? IncrementalQuantity.defaultProps.min!;
        this._maxValue = maxQuantity ?? IncrementalQuantity.defaultProps.max!;

        this._multipleQuantityValue = multipleOfQuantity ?? IncrementalQuantity.defaultProps.multipleOfQuantity!;
    }

    public render(): JSX.Element {
        const currentInput = this.state.currentInput;
        const glyphMinusClassName: string = `${this.props.decrementGlyphClass!} quantity__controls-glyph`;
        const glyphPlusClassName: string = `${this.props.incrementGlyphClass!} quantity__controls-glyph`;
        const nextDecrementValue = parseFloat(currentInput) - this._multipleQuantityValue;
        const decrementDisabled = parseFloat(currentInput) < this._minValue + 1 || this.props.isGiftCard || nextDecrementValue === 0;
        const incrementDisabled = parseFloat(currentInput) >= Number(this._maxValue) || this.props.isGiftCard;
        const currentValue = parseFloat(currentInput);
        this.payLoad.contentAction.etext = TelemetryConstant.DecrementQuantity;
        const decrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.IncrementQuantity;
        const incrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        const ariaLablelText = `${this.props.inputQuantityAriaLabel ?? ''} ${parseFloat(currentInput)}`;

        if (this.props.reset === true) {
            this.setState({ currentInput: '1.00' });
            if (this.props.handleReset) {
                this.props.handleReset();
            }
        }

        let extraClassDecrement = '';
        if (decrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassDecrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassDecrement = 'transition';
        }
        let extraClassIncrement = '';
        if (incrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassIncrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassIncrement = 'transition';
        }

        return (
            <>
                <div className='quantity' id={this.props.id}>
                    <button
                        disabled={this.props.disabled || decrementDisabled}
                        title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                        className={`decrement quantity__controls ${extraClassDecrement}`}
                        onClick={this._onDecrement}
                        aria-hidden={!!decrementDisabled}
                        aria-label={`${this.props.decrementButtonAriaLabel}`}
                        color='secondary'
                        {...decrementAttributes}
                    >
                        <span className={glyphMinusClassName} />
                    </button>
                    <input
                        type='text'
                        className='quantity-input'
                        value={currentInput}
                        onChange={this._handleChange}
                        onBlur={this._validateMin}
                        aria-live='polite'
                        aria-label={this.isMobileView() ? ariaLablelText : `${this.props.inputQuantityAriaLabel ?? ''}`}
                        role='spinbutton'
                        aria-valuemin={this._minValue}
                        aria-valuemax={this._maxValue}
                        aria-valuenow={currentValue}
                        ref={this.inputRef}
                        disabled={this.props.disabled}
                        min={this._minValue}
                        max={this._maxValue}
                    />
                    <button
                        disabled={this.props.disabled || incrementDisabled}
                        title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                        className={`increment quantity__controls ${extraClassIncrement}`}
                        onClick={this._onIncrement}
                        aria-hidden={!!incrementDisabled}
                        aria-label={`${this.props.incrementButtonAriaLabel}`}
                        color='secondary'
                        {...incrementAttributes}
                    >
                        <span className={glyphPlusClassName} />
                    </button>
                </div>
            </>
        );
    }

    /**
     * It checks if its in mobile view or not.
     * @returns Boolean.
     */
    private readonly isMobileView = (): boolean => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Added gridsetting values.
        const context = {
            gridSettings: {
                xs: { w: 767 },
                sm: { w: 991 },
                md: { w: 1199 },
                lg: { w: 1599 },
                xl: { w: 1600 }
            }
        } as IRequestContext;

        const isMobileCheck = isMobile({ variant: VariantType.Browser, context });
        return isMobileCheck === 'xs' || isMobileCheck === 'sm';
    };

    private _onIncrement(): void {
        let invokeCallback = false;
        const currQuantity = parseFloat(this.state.currentInput);

        let updatedQuantity: number;

        if (currQuantity < this.props.max) {
            invokeCallback = true;
            updatedQuantity = currQuantity + 1;
        } else {
            invokeCallback = false;
            updatedQuantity = this.props.max;
        }

        if (this._multipleQuantityValue && updatedQuantity % this._multipleQuantityValue !== 0) {
            updatedQuantity = Math.ceil(updatedQuantity / this._multipleQuantityValue) * this._multipleQuantityValue;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity.toFixed(2) });
            }
        }
    }

    private _onDecrement(): void {
        let invokeCallback = false;
        const currQuantity = parseFloat(this.state.currentInput);
        let updatedQuantity: number;

        if (currQuantity > this._minValue) {
            invokeCallback = true;
            updatedQuantity = currQuantity - 1;
        } else {
            invokeCallback = false;
            updatedQuantity = this._minValue;
        }

        if (this._multipleQuantityValue && updatedQuantity % this._multipleQuantityValue !== 0) {
            updatedQuantity = Math.floor(updatedQuantity / this._multipleQuantityValue) * this._multipleQuantityValue;
            if (updatedQuantity < this._minValue) {
                updatedQuantity = this._minValue;
            }
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity.toFixed(2) });
            }
        }
    }

    private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let value = e.target.value;

        // If the input field is empty, we update the state to an empty field.
        if (value === '') {
            this.setState({ currentInput: '' });
        } else if (/^[0-9]*[.]?[0-9]{0,2}$/.test(value)) {
            // Converts the comma to a dot and stores the valid value in the state.
            const formattedValue = value.replace(',', '.');
            this.setState({ currentInput: formattedValue });

            debounce(() => {
                this.props.onChange && this.props.onChange(parseFloat(this.state.currentInput));

                const inputElement =
                    this.inputRef && this.inputRef.current && this.inputRef.current instanceof HTMLInputElement && this.inputRef.current;
                if (inputElement) {
                    inputElement.setAttribute('aria-valuenow', this.state.currentInput);
                    inputElement.setAttribute('value', this.state.currentInput);
                }
            }, 1000)();
        }
    }

    /**
     * Validates min and updates.
     */
    private readonly _validateMin = (): void => {
        // If the input field is empty, we update the state to an empty field.
        if (this.state.currentInput === '') {
            this.setState({ currentInput: '1' });
        }

        const currentValue = parseFloat(this.state.currentInput);

        if (isNaN(currentValue)) {
            // If the value is invalid, we set it to the minimum value.
            this.setState({ currentInput: this._minValue.toFixed(2) });
        } else {
            let validValue = Math.min(this.props.max, Math.max(this._minValue, currentValue));

            // Check if the value is a multiple of multipleOfQuantity
            if (this._multipleQuantityValue && validValue % this._multipleQuantityValue !== 0) {
                validValue = Math.ceil(validValue / this._multipleQuantityValue) * this._multipleQuantityValue;
            }

            if (validValue !== currentValue) {
                this.setState({ currentInput: validValue.toFixed(2) });
            }
        }
    };
}
